import { useCallback, useEffect, useState } from 'react';
import { CreditCard, Plus, Trash2 } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import usePotvrzeni from '../ui/usePotvrzeni';
import { SECTION_COLOR, CERVENA, Nadpis, RadekPanelu, hlavicky } from '../objednavky/ui';

/**
 * FofrKarta — cashback a body (2026-10-26), Zákazníci → Věrnost.
 *
 * Souhrn v panelu (stav, body v oběhu, čekající, co brzy propadne) a okno
 * s nastavením: základní procento, platnost bodů a pravidla podle kanálu,
 * dnů a času. Platí nejkonkrétnější pravidlo, při shodě výhodnější.
 * Zapnutí není přepínač: FofrKartu zapne až přenos bodů z Objedname
 * (`php artisan fofrkarta:prenos-objedname --ostre`), jinak by se body vedly dvakrát.
 */

const KANALY = [{ id: null, label: 'Všechny kanály' }, { id: 'rozvoz', label: 'Rozvoz' }, { id: 'vydej', label: 'Výdej' }, { id: 'bistro', label: 'Bistro' }];
const DNY = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const PRAZDNE = { nazev: '', kanal: null, dny: [], od: '', do: '', procento: 10, plati_od: '', plati_do: '', aktivni: true };

const api = async (url, method = 'GET', body) => {
    const r = await fetch(url, { method, headers: hlavicky(), body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || (r.status === 403 ? 'Nemáte přístup.' : 'Nepovedlo se uložit.'));
    return d;
};

const popisPravidla = (p) => [
    KANALY.find(k => k.id === p.kanal)?.label,
    p.dny?.length ? p.dny.map(d => DNY[d - 1]).join(', ') : null,
    p.od || p.do ? `${p.od || '0:00'}–${p.do || '24:00'}` : null,
    p.plati_od || p.plati_do ? `${p.plati_od ? new Date(p.plati_od).toLocaleDateString('cs-CZ') : '…'} – ${p.plati_do ? new Date(p.plati_do).toLocaleDateString('cs-CZ') : '…'}` : null,
].filter(Boolean).join(' · ');

/** Souhrn do panelu Věrnosti + tlačítko na nastavení. */
export function FofrKartaSouhrn({ isDark, muted, cara, inputStyle }) {
    const [data, setData] = useState(null);
    const [otevreno, setOtevreno] = useState(false);
    const nacti = useCallback(() => { api('/api/fofrkarta').then(setData).catch(() => setData(false)); }, []);
    useEffect(() => { nacti(); }, [nacti]);

    if (data === false) return null; // bez přístupu se sekce neukáže
    const n = data?.nastaveni;
    return (
        <>
            <Nadpis muted={muted} style={{ marginTop: 16 }}>FofrKarta</Nadpis>
            {!data ? <p className="text-xs" style={{ color: muted }}>Načítám…</p> : (
                <>
                    <RadekPanelu popis="Stav" hodnota={n.zapnuto ? 'body vede OS' : 'body zatím z Objedname'} muted={muted} cara={cara} />
                    <RadekPanelu popis="Cashback" hodnota={`${String(n.procento).replace('.', ',')} %${data.pravidla.some(p => p.aktivni) ? ` + ${data.pravidla.filter(p => p.aktivni).length} pravidla` : ''}`} muted={muted} cara={cara} />
                    <RadekPanelu popis="Platnost bodů" hodnota={`${n.platnost_mesicu} měsíců`} muted={muted} cara={cara} />
                    {n.zapnuto && (
                        <>
                            <RadekPanelu popis="Body v oběhu" hodnota={data.souhrn.v_obehu.toLocaleString('cs-CZ')} muted={muted} cara={cara} />
                            <RadekPanelu popis="Čeká na dokončení objednávek" hodnota={data.souhrn.ceka.toLocaleString('cs-CZ')} muted={muted} cara={cara} />
                            <RadekPanelu popis="Propadne do 30 dní" hodnota={data.souhrn.propadne_30_dni.toLocaleString('cs-CZ')} muted={muted} cara={cara} />
                        </>
                    )}
                    <button type="button" onClick={() => setOtevreno(true)} className="rounded-lg"
                        style={{ marginTop: 10, minHeight: 36, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: SECTION_COLOR, background: 'transparent', border: `1px solid ${SECTION_COLOR}` }}>
                        Nastavení cashbacku
                    </button>
                </>
            )}
            {otevreno && data && (
                <NastaveniOkno data={data} isDark={isDark} muted={muted} cara={cara} inputStyle={inputStyle}
                    onZmena={setData} onClose={() => { setOtevreno(false); nacti(); }} />
            )}
        </>
    );
}

function NastaveniOkno({ data, isDark, muted, cara, inputStyle, onZmena, onClose }) {
    const [procento, setProcento] = useState(String(data.nastaveni.procento));
    const [platnost, setPlatnost] = useState(String(data.nastaveni.platnost_mesicu));
    const [upravovane, setUpravovane] = useState(null); // pravidlo ve formuláři
    const [chyba, setChyba] = useState(null);
    const [prace, setPrace] = useState(false);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    const ulozNastaveni = async () => {
        setPrace(true); setChyba(null);
        try { onZmena(await api('/api/fofrkarta/nastaveni', 'PUT', { procento: Number(procento.replace(',', '.')), platnost_mesicu: Number(platnost) })); }
        catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };
    const ulozPravidlo = async () => {
        setPrace(true); setChyba(null);
        const p = upravovane;
        const telo = { ...p, procento: Number(String(p.procento).replace(',', '.')), od: p.od || null, do: p.do || null, plati_od: p.plati_od || null, plati_do: p.plati_do || null };
        try {
            await api(p.id ? `/api/fofrkarta/pravidla/${p.id}` : '/api/fofrkarta/pravidla', p.id ? 'PUT' : 'POST', telo);
            onZmena(await api('/api/fofrkarta'));
            setUpravovane(null);
        } catch (e) { setChyba(e.message); }
        finally { setPrace(false); }
    };
    const smaz = async (p) => {
        if (!(await potvrdit(`Smazat pravidlo „${p.nazev}“?`, { text: 'Nové objednávky ho přestanou používat. Už připsané body zůstanou.', potvrdit: 'Smazat' }))) return;
        try { await api(`/api/fofrkarta/pravidla/${p.id}`, 'DELETE'); onZmena(await api('/api/fofrkarta')); setUpravovane(null); }
        catch (e) { setChyba(e.message); }
    };

    const tlacitko = (primarni) => ({
        minHeight: 40, padding: '0 16px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
        color: primarni ? '#fff' : 'var(--text-secondary)', background: primarni ? SECTION_COLOR : 'transparent',
        border: primarni ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, opacity: prace ? 0.6 : 1,
    });
    const pole = { ...inputStyle, height: 36 };
    const nastav = (k, v) => setUpravovane(x => ({ ...x, [k]: v }));

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis="FofrKarta · cashback" ikona={CreditCard} barva={SECTION_COLOR} sirka={560}
            paticka={upravovane ? (
                <div className="flex gap-2 justify-end">
                    <button type="button" style={tlacitko(false)} onClick={() => setUpravovane(null)}>Zpět</button>
                    <button type="button" style={tlacitko(true)} disabled={prace || !upravovane.nazev.trim()} onClick={ulozPravidlo}>{upravovane.id ? 'Uložit pravidlo' : 'Přidat pravidlo'}</button>
                </div>
            ) : (
                <div className="flex gap-2 justify-end">
                    <button type="button" style={tlacitko(false)} onClick={onClose}>Zavřít</button>
                </div>
            )}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
                {!data.nastaveni.zapnuto && (
                    <p style={{ margin: 0, padding: '10px 12px', borderRadius: 8, color: 'var(--text-secondary)', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                        Body zatím vede Objedname. FofrKarta se zapne přenosem zůstatků z Objedname — od té chvíle se cashback připisuje podle tohoto nastavení.
                    </p>
                )}
                {upravovane ? (
                    <>
                        <Popis text="Název" muted={muted}><input id="fk-nazev" value={upravovane.nazev} onChange={e => nastav('nazev', e.target.value)} maxLength={80} placeholder="Např. Odpolední výdej" style={pole} /></Popis>
                        <Popis text="Kanál" muted={muted}>
                            <Cipy moznosti={KANALY} vybrano={(id) => upravovane.kanal === id} onClick={(id) => nastav('kanal', id)} isDark={isDark} />
                        </Popis>
                        <Popis text="Dny (nic = každý den)" muted={muted}>
                            <Cipy moznosti={DNY.map((d, i) => ({ id: i + 1, label: d }))} vybrano={(id) => upravovane.dny.includes(id)}
                                onClick={(id) => nastav('dny', upravovane.dny.includes(id) ? upravovane.dny.filter(x => x !== id) : [...upravovane.dny, id].sort())} isDark={isDark} />
                        </Popis>
                        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                            <Popis text="Od" muted={muted}><input id="fk-od" type="time" value={upravovane.od || ''} onChange={e => nastav('od', e.target.value)} style={pole} /></Popis>
                            <Popis text="Do" muted={muted}><input id="fk-do" type="time" value={upravovane.do || ''} onChange={e => nastav('do', e.target.value)} style={pole} /></Popis>
                            <Popis text="Cashback %" muted={muted}><input id="fk-pct" inputMode="decimal" value={upravovane.procento} onChange={e => nastav('procento', e.target.value)} style={pole} /></Popis>
                        </div>
                        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <Popis text="Platí od (nepovinné)" muted={muted}><input id="fk-plod" type="date" value={upravovane.plati_od || ''} onChange={e => nastav('plati_od', e.target.value)} style={pole} /></Popis>
                            <Popis text="Platí do (nepovinné)" muted={muted}><input id="fk-pldo" type="date" value={upravovane.plati_do || ''} onChange={e => nastav('plati_do', e.target.value)} style={pole} /></Popis>
                        </div>
                        <label className="flex items-center" style={{ gap: 8, minHeight: 32, color: 'var(--text-primary)' }}>
                            <input id="fk-aktivni" type="checkbox" checked={upravovane.aktivni} onChange={e => nastav('aktivni', e.target.checked)} /> Pravidlo platí
                        </label>
                        {upravovane.id && (
                            <button type="button" onClick={() => smaz(upravovane)} className="inline-flex items-center self-start"
                                style={{ gap: 6, minHeight: 32, fontSize: 12.5, color: CERVENA, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <Trash2 size={13} /> Smazat pravidlo
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                            <Popis text="Základní cashback %" muted={muted}><input id="fk-zaklad" inputMode="decimal" value={procento} onChange={e => setProcento(e.target.value)} style={pole} /></Popis>
                            <Popis text="Platnost bodů (měsíce)" muted={muted}><input id="fk-platnost" type="number" min={1} max={60} value={platnost} onChange={e => setPlatnost(e.target.value)} style={pole} /></Popis>
                            <button type="button" style={tlacitko(true)} disabled={prace} onClick={ulozNastaveni}>Uložit</button>
                        </div>
                        <p style={{ margin: 0, fontSize: 12, color: muted }}>
                            Cashback se počítá z jídla bez balného a dopravy, po slevě, a připíše se až po dokončení objednávky. Z objednávky placené body se nepřipisuje nic.
                        </p>
                        <div>
                            <Nadpis muted={muted}>Pravidla</Nadpis>
                            <p style={{ margin: '0 0 6px', fontSize: 12, color: muted }}>Platí nejkonkrétnější pravidlo (kanál, dny a čas), při shodě to výhodnější. Jinak základ.</p>
                            {data.pravidla.length === 0 && <p style={{ margin: '6px 0', color: muted }}>Zatím žádná — všude platí základ.</p>}
                            {data.pravidla.map(p => (
                                <button key={p.id} type="button" onClick={() => setUpravovane({ ...PRAZDNE, ...p, dny: p.dny || [] })} className="flex items-center w-full text-left"
                                    style={{ gap: 10, minHeight: 44, padding: '4px 0', background: 'transparent', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer', opacity: p.aktivni ? 1 : 0.5 }}>
                                    <span className="flex-1 min-w-0">
                                        <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-primary)' }}>{p.nazev}{p.aktivni ? '' : ' · vypnuté'}</span>
                                        <span className="truncate" style={{ display: 'block', fontSize: 12, color: muted }}>{popisPravidla(p)}</span>
                                    </span>
                                    <b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{String(p.procento).replace('.', ',')} %</b>
                                </button>
                            ))}
                            <button type="button" onClick={() => setUpravovane({ ...PRAZDNE })} className="inline-flex items-center"
                                style={{ gap: 6, marginTop: 8, minHeight: 36, fontSize: 13, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                                <Plus size={14} /> Přidat pravidlo
                            </button>
                        </div>
                    </>
                )}
                {chyba && <p role="alert" style={{ margin: 0, fontSize: 12.5, color: CERVENA }}>{chyba}</p>}
            </div>
            {potvrzeni}
        </ModalShell>
    );
}

function Popis({ text, muted, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: muted }}>{text}</span>
            {children}
        </label>
    );
}

function Cipy({ moznosti, vybrano, onClick, isDark }) {
    return (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
            {moznosti.map(m => {
                const on = vybrano(m.id);
                return (
                    <button key={String(m.id)} type="button" onClick={() => onClick(m.id)}
                        style={{
                            height: 32, padding: '0 11px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                            color: on ? '#fff' : 'var(--text-secondary)', background: on ? SECTION_COLOR : 'transparent',
                            border: `1px solid ${on ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                        }}>{m.label}</button>
                );
            })}
        </div>
    );
}
